import { useState, useEffect, useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import { normalizarBooleano } from 'utils/general'
import { requiereEnvio as requiereEnvioFn } from 'config/pedidos'

/**
 * Hook personalizado para manejar el estado y lógica de formularios de pedidos
 * Funciona tanto para crear nuevos pedidos como para editar existentes
 */
export function usePedidoForm({
	modo = 'nuevo', // 'nuevo' | 'editar'
	pedidoOriginal = null,
	onSave,
	onCancel
}) {
	// Estados del formulario
	const [loading, setLoading] = useState(false)
	const [erroresValidacion, setErroresValidacion] = useState({})
	const [activeTab, setActiveTab] = useState('general')
	const [inicializado, setInicializado] = useState(false)

	// Estados de datos
	const [datosGenerales, setDatosGenerales] = useState({
		tipo: '',
		metodoPago: '',
		observaciones: '', // Observaciones del cliente
		observacionesInternas: '' // Observaciones internas de la tienda
	})

	const [cliente, setCliente] = useState(null)

	const [items, setItems] = useState([]) // Items originales (para modo editar)
	const [itemsModificados, setItemsModificados] = useState({
		items: [], // Nuevos items agregados
		eliminarItemsIds: [], // IDs de items a eliminar
		modificarItems: [] // Items existentes modificados
	})

	const [puntoEntrega, setPuntoEntrega] = useState(null)

	// Estado para costo de envío manual
	const [costoEnvioManual, setCostoEnvioManual] = useState(false)

	const [datosFacturacion, setDatosFacturacion] = useState({
		razonSocial: '',
		ruc: ''
	})

	// Estados del pedido (para modo editar)
	const [estadosModificados, setEstadosModificados] = useState({})

	// Función para resetear formulario (memoizada)
	const resetFormulario = useCallback(() => {
		setDatosGenerales({ tipo: '', metodoPago: '', observaciones: '', observacionesInternas: '' })
		setCliente(null)
		setItems([])
		setItemsModificados({ items: [], eliminarItemsIds: [], modificarItems: [] })
		setPuntoEntrega(null)
		setCostoEnvioManual(false)
		setDatosFacturacion({ razonSocial: '', ruc: '' })
		setEstadosModificados({})
		setErroresValidacion({})
		setActiveTab('general')
		setInicializado(false)
	}, [])

	// Efecto para inicializar datos cuando cambia el pedido original
	useEffect(() => {
		if (modo === 'editar' && pedidoOriginal && pedidoOriginal.id) {
			// Inicializar datos generales
			setDatosGenerales({
				tipo: pedidoOriginal.tipo || '',
				metodoPago: pedidoOriginal.metodoPago || '',
				observaciones: pedidoOriginal.observaciones || '', // Del cliente
				observacionesInternas: pedidoOriginal.observacionesInternas || '' // Internas
			})

			// Inicializar cliente
			setCliente(pedidoOriginal.usuario || null)

			// Inicializar items
			setItems(pedidoOriginal.items || [])
			setItemsModificados({
				items: [],
				eliminarItemsIds: [],
				modificarItems: []
			})

			// Inicializar punto de entrega
			setPuntoEntrega(pedidoOriginal.puntoEntrega || null)

			// Inicializar costo de envío manual
			setCostoEnvioManual(pedidoOriginal.costoEnvioManual || false)

			// Inicializar datos de facturación
			setDatosFacturacion({
				razonSocial: pedidoOriginal.datosFacturacion?.razonSocial || '',
				ruc: pedidoOriginal.datosFacturacion?.ruc || ''
			})

			// Inicializar estados modificados vacío
			setEstadosModificados({})

			// Limpiar errores
			setErroresValidacion({})

			// Asegurar que estÃ© en la pestaña general
			setActiveTab('general')

			// Marcar como inicializado al final
			setInicializado(true)
		} else if (modo === 'nuevo') {
			// Reset para modo nuevo
			setDatosGenerales({ tipo: '', metodoPago: '', observaciones: '', observacionesInternas: '' })
			setCliente(null)
			setItems([])
			setItemsModificados({ items: [], eliminarItemsIds: [], modificarItems: [] })
			setPuntoEntrega(null)
			setCostoEnvioManual(false)
			setDatosFacturacion({ razonSocial: '', ruc: '' })
			setEstadosModificados({})
			setErroresValidacion({})
			setActiveTab('general')
			setInicializado(true)
		}
	}, [modo, pedidoOriginal?.id, pedidoOriginal?.tipo, pedidoOriginal?.metodoPago, pedidoOriginal?.observaciones, pedidoOriginal?.observacionesInternas])

	// Función para obtener el pedido actualizado con estados modificados
	const getPedidoActualizado = useCallback(() => {
		if (!pedidoOriginal) return null

		return {
			...pedidoOriginal,
			...estadosModificados // Aplicar cambios de estados
		}
	}, [pedidoOriginal, estadosModificados])

	// Función para asegurar que itemsModificados siempre tenga la estructura correcta
	const asegurarEstructuraItems = useCallback((items) => {
		return {
			items: items?.items || [],
			eliminarItemsIds: items?.eliminarItemsIds || [],
			modificarItems: items?.modificarItems || []
		}
	}, [])

	// Calcular totales automáticamente
	const totales = useMemo(() => {
		let subtotal = 0
		let cantidadItems = 0
		let cantidadProductos = 0

		// Asegurar estructura correcta
		const itemsSeguro = asegurarEstructuraItems(itemsModificados)

		if (modo === 'editar') {
			// Para modo editar: procesar items originales considerando modificaciones y eliminaciones
			items.forEach(item => {
				// Saltar items marcados para eliminar
				if (itemsSeguro.eliminarItemsIds.includes(item.id)) {
					return
				}

				// Verificar si el item fue modificado
				const itemModificado = itemsSeguro.modificarItems.find(mod => mod.id === item.id)

				if (itemModificado) {
					// Usar los datos modificados
					const cantidad = parseInt(itemModificado.cantidad || 0)
					const precio = parseFloat(itemModificado.monto || 0)

					subtotal += precio * cantidad
					cantidadItems += cantidad
					cantidadProductos += 1
				} else {
					// Usar los datos originales
					const cantidad = parseInt(item.cantidad || 0)
					const precio = parseFloat(item.monto || 0)

					subtotal += precio * cantidad
					cantidadItems += cantidad
					cantidadProductos += 1
				}
			})
		}

		// Sumar items completamente nuevos (tanto para modo nuevo como editar)
		itemsSeguro.items.forEach(item => {
			const cantidad = parseInt(item.cantidad || 0)
			const precio = parseFloat(item.monto || 0)

			subtotal += precio * cantidad
			cantidadItems += cantidad
			cantidadProductos += 1
		})

		// Agregar costo de envío
		const costoEnvio = parseFloat(puntoEntrega?.costoEnvio || 0)
		const total = subtotal + costoEnvio

		return {
			subtotal,
			costoEnvio,
			total,
			cantidadItems,
			cantidadProductos
		}
	}, [items, itemsModificados, puntoEntrega?.costoEnvio, modo, asegurarEstructuraItems])

	// Detectar cambios para habilitar/deshabilitar botón de guardar
	const hayCambios = useMemo(() => {
		// No calcular hasta estar inicializado
		if (!inicializado) return false

		// Asegurar estructura correcta
		const itemsSeguro = asegurarEstructuraItems(itemsModificados)

		if (modo === 'nuevo') {
			// En modo nuevo, hay cambios si hay datos básicos
			const tieneUsuario = !!cliente
			const tieneTipo = !!datosGenerales.tipo
			const tieneObservaciones = !!datosGenerales.observaciones
			const tieneObservacionesInternas = !!datosGenerales.observacionesInternas
			const tieneItems = itemsSeguro.items.length > 0
			const tienePuntoEntrega = !!puntoEntrega?.direccion
			const tieneFacturacion = !!(datosFacturacion.razonSocial || datosFacturacion.ruc)

			const resultado = tieneTipo || tieneUsuario || tieneItems || tienePuntoEntrega || tieneFacturacion || tieneObservaciones || tieneObservacionesInternas

			return resultado
		} else {
			// En modo editar, comparar con valores originales
			const tipoOriginal = pedidoOriginal?.tipo || ''
			const observacionesOriginales = pedidoOriginal?.observaciones || ''
			const observacionesInternasOriginales = pedidoOriginal?.observacionesInternas || ''
			const clienteOriginalId = pedidoOriginal?.usuario?.id
			const puntoEntregaOriginal = pedidoOriginal?.puntoEntrega
			const facturacionOriginal = pedidoOriginal?.datosFacturacion

			const metodoPagoOriginal = pedidoOriginal?.metodoPago || ''
			const cambiosGenerales =
				datosGenerales.tipo !== tipoOriginal ||
				datosGenerales.metodoPago !== metodoPagoOriginal ||
				datosGenerales.observaciones !== observacionesOriginales ||
				datosGenerales.observacionesInternas !== observacionesInternasOriginales

			const cambiosCliente = cliente?.id !== clienteOriginalId

			const cambiosItems =
				itemsSeguro.items.length > 0 ||
				itemsSeguro.eliminarItemsIds.length > 0 ||
				itemsSeguro.modificarItems.length > 0

			// Detectar cambios en estados
			const cambiosEstados = Object.keys(estadosModificados).length > 0

			// Detectar cambios en costo de envío manual
			const costoEnvioManualOriginal = pedidoOriginal?.costoEnvioManual || false
			const costoEnvioOriginal = pedidoOriginal?.costoEnvio || 0
			const costoEnvioActual = puntoEntrega?.costoEnvio || 0

			const cambiosCostoEnvio =
				costoEnvioManual !== costoEnvioManualOriginal ||
				(costoEnvioManual && costoEnvioActual !== costoEnvioOriginal)

			// Función para limpiar propiedades calculadas del punto de entrega
			const limpiarPuntoEntrega = (punto) => {
				if (!punto) return null
				// Excluir propiedades calculadas automáticamente
				const { areaId, costoEnvio, metodoCalculo, detallesCalculo, ...puntoLimpio } = punto
				return puntoLimpio
			}

			// Comparación más cuidadosa del punto de entrega (sin propiedades calculadas)
			const puntoEntregaActualLimpio = limpiarPuntoEntrega(puntoEntrega)
			const puntoEntregaOriginalLimpio = limpiarPuntoEntrega(puntoEntregaOriginal)

			const puntoEntregaActualStr = JSON.stringify(puntoEntregaActualLimpio)
			const puntoEntregaOriginalStr = JSON.stringify(puntoEntregaOriginalLimpio)
			const cambiosPuntoEntrega = puntoEntregaActualStr !== puntoEntregaOriginalStr

			const cambiosFacturacion =
				datosFacturacion.razonSocial !== (facturacionOriginal?.razonSocial || '') ||
				datosFacturacion.ruc !== (facturacionOriginal?.ruc || '')

			const resultado = cambiosGenerales || cambiosCliente || cambiosItems || cambiosPuntoEntrega || cambiosFacturacion || cambiosEstados || cambiosCostoEnvio

			return resultado
		}
	}, [inicializado, datosGenerales, cliente, itemsModificados, puntoEntrega, datosFacturacion, estadosModificados, pedidoOriginal, modo, asegurarEstructuraItems, costoEnvioManual])

	// Handlers para cambios de datos (memoizados para evitar renders infinitos)
	const handleDatosGeneralesChange = useCallback((nuevosDatos) => {
		setDatosGenerales(prev => {
			const newState = { ...prev, ...nuevosDatos }
			return newState
		})

		// Limpiar errores relacionados
		if (nuevosDatos.tipo) {
			setErroresValidacion(prev => ({ ...prev, tipo: null }))
		}
	}, [])

	const handleClienteChange = useCallback((nuevoCliente) => {
		setCliente(nuevoCliente)

		// Limpiar errores relacionados
		setErroresValidacion(prev => ({ ...prev, cliente: null, usuarioId: null }))
	}, [])

	const handleItemsChange = useCallback((nuevosItemsModificados) => {
		// Asegurar que siempre tenga la estructura correcta
		const itemsSeguro = asegurarEstructuraItems(nuevosItemsModificados)
		setItemsModificados(itemsSeguro)

		// Limpiar errores relacionados
		setErroresValidacion(prev => ({ ...prev, items: null }))
	}, [asegurarEstructuraItems])

	const handlePuntoEntregaChange = useCallback((nuevoPuntoEntrega) => {
		setPuntoEntrega(nuevoPuntoEntrega)

		// Limpiar errores relacionados si hay dirección
		if (nuevoPuntoEntrega?.direccion) {
			setErroresValidacion(prev => ({ ...prev, puntoEntrega: null }))
		}
	}, [])

	const handleDatosFacturacionChange = useCallback((nuevosDatos) => {
		setDatosFacturacion(prev => ({ ...prev, ...nuevosDatos }))
	}, [])

	// Handler para cambiar costo de envío manual
	const handleCostoEnvioManualChange = useCallback((esManual, costoManual = null) => {
		setCostoEnvioManual(esManual)

		// Si se activa el modo manual y hay un costo especificado, actualizar el puntoEntrega
		if (esManual && costoManual !== null && puntoEntrega) {
			setPuntoEntrega(prev => ({
				...prev,
				costoEnvio: costoManual
			}))
		}
	}, [puntoEntrega])

	// Handler para cambios de estado
	const handleEstadoChange = useCallback((campo, valor) => {
		setEstadosModificados(prev => ({
			...prev,
			[campo]: valor
		}))
	}, [])

	// Validaciones
	const validarFormulario = useCallback(() => {
		const errores = {}

		// Asegurar estructura correcta
		const itemsSeguro = asegurarEstructuraItems(itemsModificados)

		// Validar tipo de pedido
		if (!datosGenerales.tipo) {
			errores.tipo = 'El tipo de pedido es obligatorio'
		}

		// Validar método de pago
		if (!datosGenerales.metodoPago) {
			errores.metodoPago = 'El método de pago es obligatorio'
		}

		// Validar items
		const totalItems = modo === 'nuevo'
			? itemsSeguro.items.length
			: items.length - itemsSeguro.eliminarItemsIds.length + itemsSeguro.items.length

		if (totalItems === 0) {
			errores.items = 'Debe agregar al menos un producto al pedido'
		}

		// Validar punto de entrega si es requerido
		if (requiereEnvioFn(datosGenerales.tipo)) {
			if (!puntoEntrega?.direccion) {
				errores.puntoEntrega = 'La dirección de entrega es obligatoria para este tipo de pedido'
			} else if (puntoEntrega?.areaId === null || puntoEntrega?.areaId === undefined) {
				errores.puntoEntrega = 'La dirección debe estar dentro del área de cobertura'
			}
		}

		setErroresValidacion(errores)
		return Object.keys(errores).length === 0
	}, [datosGenerales, cliente, items, itemsModificados, puntoEntrega, modo, asegurarEstructuraItems])

	// Handler para guardar (memoizado)
	const handleSave = useCallback(async () => {
		if (!validarFormulario()) {
			toast.error('Por favor corrija los errores antes de guardar')
			return
		}

		try {
			setLoading(true)

			// Asegurar estructura correcta
			const itemsSeguro = asegurarEstructuraItems(itemsModificados)

			// Preparar datos para enviar
			const datosParaEnviar = {
				// Datos generales
				tipo: datosGenerales.tipo,
				metodoPago: datosGenerales.metodoPago,
				observaciones: datosGenerales.observaciones, // Del cliente
				observacionesInternas: datosGenerales.observacionesInternas, // Internas

				// Cliente
				usuarioId: cliente?.id || null,

				// Items
				...(modo === 'nuevo' ? {
					items: itemsSeguro.items
				} : {
					// Para modo editar, enviar cambios
					agregarItems: itemsSeguro.items,
					eliminarItemsIds: itemsSeguro.eliminarItemsIds,
					modificarItems: itemsSeguro.modificarItems
				}),

				// Punto de entrega (sin costoEnvio, va en el nivel superior)
				puntoEntrega: puntoEntrega ? {
					...puntoEntrega,
					// Remover costoEnvio del puntoEntrega ya que va en el nivel superior
					costoEnvio: undefined
				} : null,

				// Datos de facturación
				datosFacturacion: datosFacturacion,

				// Estados modificados (solo para modo editar)
				...(modo === 'editar' && Object.keys(estadosModificados).length > 0 && {
					estadosModificados: estadosModificados
				}),

				// Costo de envío en el nivel superior del pedido
				costoEnvio: totales.costoEnvio,
				costoEnvioManual: costoEnvioManual,

				// Totales calculados
				subtotal: totales.subtotal,
				montoTotal: totales.total
			}

			await onSave(datosParaEnviar)

			// Si es modo nuevo y el guardado fue exitoso, resetear formulario
			if (modo === 'nuevo') {
				resetFormulario()
			}

		} catch (error) {
			console.error(`Error al ${modo === 'nuevo' ? 'crear' : 'actualizar'} pedido:`, error)
			toast.error(`Error al ${modo === 'nuevo' ? 'crear' : 'actualizar'} el pedido`)
		} finally {
			setLoading(false)
		}
	}, [validarFormulario, datosGenerales, cliente, itemsModificados, puntoEntrega, datosFacturacion, estadosModificados, totales, costoEnvioManual, onSave, modo, asegurarEstructuraItems, resetFormulario])

	// Handler para cancelar (memoizado)
	const handleCancel = useCallback(() => {
		if (modo === 'nuevo') {
			resetFormulario()
		}
		onCancel()
	}, [onCancel, modo, resetFormulario])

	return {
		// Estado
		loading,
		erroresValidacion,
		activeTab,
		setActiveTab,

		// Datos
		datosGenerales,
		cliente,
		items,
		itemsModificados,
		puntoEntrega,
		datosFacturacion,
		costoEnvioManual,

		pedidoActualizado: getPedidoActualizado(),

		// Totales calculados
		totales,

		// Flags
		hayCambios,
		inicializado, // Exportar para debug si es necesario

		// Handlers
		handleDatosGeneralesChange,
		handleClienteChange,
		handleItemsChange,
		handlePuntoEntregaChange,
		handleDatosFacturacionChange,
		handleCostoEnvioManualChange,
		handleEstadoChange,
		handleSave,
		handleCancel,

		// Utilidades
		validarFormulario,
		resetFormulario
	}
}